import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { useRequest } from 'ahooks'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import authorization from 'live/services/authorization'
import { StaticDataType, STATIC_DATA_STATES } from '../chart-components'
import { CustomModalsProps, GenericWarning, ModalTabSections } from '../modal-components'
import { ModalHeader, ModalBody, ModalFooter, ModalButton } from '../modal-components-style'
import { StaticDataContext, useStaticDataWidget } from '../../context/StaticDataContext'
import { createSingleEntity, SECTION_API, updateSingleEntity } from '../../api/api'
import { fetchSectionTimestamps } from '../../api/phaseApi'
import { RawPhaseEvent } from '../../utils/type'
import { emptyPhaseErrors, PhaseErrors, emptyRawPhaseEvent, Errors, getEmptyErrors } from './types'
import PhaseTab from './PhaseTab'

const errorMessage = i18n('Please correct the highlighted fields')

interface PhaseModalProps extends CustomModalsProps {
    event: RawPhaseEvent
    closeModal: (sectionData?: any, action?: string) => void
}

export const PhaseModal = ({ title = StaticDataType.PHASE, closeModal, event }: PhaseModalProps): ReactElement => {
    const { reloadData } = useStaticDataWidget()
    const [errors, setErrors] = useState(getEmptyErrors())
    const [noPreviousCasing, setNoPreviousCasing] = useState(
        event?.id ? event.prevCasingOD === undefined && event.prevCasingFinalMD === undefined : false
    )
    const [wellOnShore, setWellOnShore] = useState(false)
    const { liveTheme } = useContext(StaticDataContext)

    const hasPermissionToEdit =
        authorization.superAdmin ||
        authorization.admin ||
        authorization.Permission.ADMIN ||
        authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
        authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

    const [phaseRawEvent, setPhaseRawEvent] = useState<RawPhaseEvent>(
        event?.id
            ? event
            : {
                  ...emptyRawPhaseEvent,
                  ...event
              }
    )

    const { data: result } = useRequest(() => fetchSectionTimestamps(event))

    const updatePhaseEvent = useCallback((newEvent: RawPhaseEvent) => {
        setPhaseRawEvent(newEvent)
    }, [])

    const postPhase = (rawPhase: RawPhaseEvent): any => {
        const phaseData = (({ state, ...rawPhase }) => rawPhase)(rawPhase)

        return (rawPhase.id ? updateSingleEntity : createSingleEntity)(SECTION_API, phaseData)
    }

    const validateFields = useCallback((event: RawPhaseEvent, noPreviousCasing: boolean, wellOnShore: boolean): {
        pass: boolean
        errors: Errors
    } => {
        const errors: Errors = getEmptyErrors()
        const { pass: passPhase, errors: phaseErrors } = validatePhaseFields(event, noPreviousCasing, wellOnShore)
        if (!passPhase) {
            errors.messages.phase = errorMessage
            errors.phase = phaseErrors
        }

        return {
            pass: passPhase,
            errors: errors
        }
    }, [])

    const validatePhaseFields = useCallback((event: RawPhaseEvent, noPreviousCasing: boolean, wellOnShore: boolean): {
        pass: boolean
        errors: PhaseErrors
    } => {
        let pass = true
        let errors: PhaseErrors = emptyPhaseErrors

        if (!event.oD.value || !event.oD.uom) {
            pass = false
            errors = { ...errors, oD: true }
        }

        if (!event.start) {
            pass = false
            errors = { ...errors, start: true }
        }

        if (!noPreviousCasing && !event.prevCasingOD?.value) {
            pass = false
            errors = { ...errors, prevCasingOD: true }
        }

        if (!noPreviousCasing && !event.prevCasingFinalMD?.value) {
            pass = false
            errors = { ...errors, prevCasingFinalMD: true }
        }

        if (!event.riser && !noPreviousCasing && !wellOnShore) {
            pass = false
            errors = { ...errors, riser: true }
        }

        return {
            pass: pass,
            errors: errors
        }
    }, [])

    useEffect(() => {
        if (result?.start) setPhaseRawEvent(prevstate => ({ ...prevstate, start: result.start }))
        if (result?.end) setPhaseRawEvent(prevstate => ({ ...prevstate, end: result.end }))
    }, [result?.end, result?.start])

    const btn =
        event.state != STATIC_DATA_STATES.EMPTY
            ? event.state !== STATIC_DATA_STATES.FINISHED
                ? 'Finish'
                : 'Save'
            : 'Start'

    return (
        <>
            <PhaseModalContainer>
                <ModalHeader>{i18n(title).toUpperCase()}</ModalHeader>
                <ModalBody liveTheme={liveTheme}>
                    <ModalTabSections
                        selected
                        content={
                            <PhaseTab
                                event={phaseRawEvent}
                                errors={errors.phase}
                                updatePhaseEvent={updatePhaseEvent}
                                setNoPreviousCasing={value => setNoPreviousCasing(value)}
                                noPreviousCasing={noPreviousCasing}
                                setWellOnShore={value => setWellOnShore(value)}
                                hasPermissionToEdit={hasPermissionToEdit}
                            />
                        }
                    />
                    <GenericWarning message={errors?.messages?.phase} />
                </ModalBody>
                <ModalFooter liveTheme={liveTheme}>
                    {hasPermissionToEdit && (
                        <ModalButton
                            styleType="action"
                            onClick={() => {
                                const validation = validateFields(phaseRawEvent, noPreviousCasing, wellOnShore)
                                if (validation.pass) {
                                    setErrors(getEmptyErrors())
                                    postPhase(phaseRawEvent).then(() => {
                                        closeModal()
                                        reloadData()
                                    })
                                } else {
                                    setErrors(validation.errors)
                                }
                            }}
                        >
                            {i18n(btn)}
                        </ModalButton>
                    )}
                    <ModalButton styleType="cancel" onClick={closeModal}>
                        {hasPermissionToEdit ? i18n('Cancel') : i18n('Close')}
                    </ModalButton>
                </ModalFooter>
            </PhaseModalContainer>
        </>
    )
}

const PhaseModalContainer = styled.div`
    height: 100%;
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
    }
    input[type='number'] {
        -moz-appearance: textfield;
        border-radius: 3px;
    }
    .input-checkbox {
        gap: 9px;
        input:checked {
            color: white;
            border-color: ${({ theme }) => theme['link-button']};
            background: ${({ theme }) => theme['link-button']};
        }
        input[type='checkbox']:not(.browser-default-input-style):checked::after {
            content: '';
            color: white;
        }
    }
    input[type='checkbox']:not(.browser-default-input-style) {
        margin-bottom: -2px;
        border-radius: 3px;
        margin-right: 8px;
    }

    .no-previous-casing-checkbox {
        font-weight: 400;
        font-size: 13px;

        color: ${({ theme }) => theme['auxiliar-typography']};
    }
`
